import type { Maybe } from '../graphql';
import { Currency } from '../graphql';
import { nonWeakMemoize3 } from '@seaber/common/lib/memoize';

const defaultCurrencyFormatter = new Intl.NumberFormat('fi-FI', {
  style: 'currency',
  currency: Currency.EUR,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const getCurrencyFormatter = nonWeakMemoize3(function getCurrencyFormatter(
  currency: Currency,
  fractionDigits: number,
  opts?: Partial<Intl.NumberFormatOptions>,
) {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    ...opts,
  });
});

export const formatCurrency = (
  number: number | null,
  fractionDigits: number,
  currency: Maybe<Currency> | undefined,
  opts?: Partial<Intl.NumberFormatOptions> & { signAlways?: boolean },
): string | null => {
  if (number === null || isNaN(number)) return null;
  if (!currency) {
    currency = Currency.EUR;
  }

  const formatter =
    fractionDigits === 0 && currency === Currency.EUR
      ? defaultCurrencyFormatter
      : getCurrencyFormatter(currency, fractionDigits, opts);

  return (
    (opts?.signAlways && number >= 0 ? '+' : '') + formatter.format(number)
  );
};

const defaultFormatter = new Intl.NumberFormat('fi-FI', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  minimumSignificantDigits: undefined,
  maximumSignificantDigits: undefined,
});

const getNumberFormatter = nonWeakMemoize3(function getNumberFormatter(
  fractionDigits: number,
  significantDigits: number | undefined = undefined,
  locales: Intl.LocalesArgument = 'fi-FI',
) {
  return new Intl.NumberFormat(locales, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    minimumSignificantDigits: significantDigits,
    maximumSignificantDigits: significantDigits,
  });
});

/** Same as getNumberFormatter but with support for different min and max digits */
export const getNumberFormatter2 = nonWeakMemoize3(function getNumberFormatter(
  minFractionDigits: number,
  maxFractionDigits: number,
  minSignificantDigits: number | undefined = undefined,
  maxSignificantDigits: number | undefined = undefined,
  locales: Intl.LocalesArgument = 'fi-FI',
) {
  return new Intl.NumberFormat(locales, {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
    minimumSignificantDigits: minSignificantDigits,
    maximumSignificantDigits: maxSignificantDigits,
  });
});

export const formatNumber = (
  number: number | null,
  fractionDigits = 0,
  significantDigits: number | undefined = undefined,
  locales: Intl.LocalesArgument | undefined = undefined,
): string | null => {
  if (number === null || isNaN(number)) return null;

  const formatter =
    fractionDigits === 0 &&
    significantDigits === undefined &&
    locales === undefined
      ? defaultFormatter
      : getNumberFormatter(fractionDigits, significantDigits, locales);

  return formatter.format(number);
};

export const parseCurrencyString = (
  formattedValue: string,
  currency: Currency = Currency.EUR,
): number | null => {
  const cleanedString = formattedValue.replace(
    new RegExp(`[^\\d${currency === Currency.EUR ? ',' : '.'}]`, 'g'),
    '',
  );
  const normalizedString =
    currency === Currency.EUR ? cleanedString.replace(',', '.') : cleanedString;

  const number = parseFloat(normalizedString);
  return isNaN(number) ? null : number;
};

/** Pluralizes the last string in the template literal according to the count.
 * Examples:
 * pluralize`${count} leg` => `1 leg` or `2 legs`
 * pluralize`Optimizing ${count} leg` => `Optimizing 1 leg` or `Optimizing 2 legs` */
export const pluralize = (
  strings: TemplateStringsArray,
  countExpr: unknown,
): string => {
  const unit = strings.at(-1);
  if (!unit) {
    console.warn('pluralize: no unit');
    return `${countExpr}`;
  }
  const count = Number(countExpr);
  if (isNaN(count)) {
    console.warn(`pluralize: count ${countExpr} is not a number`);
    return `${countExpr}`;
  }
  const pluralizedUnit = count === 1 ? unit : `${unit}s`;
  return strings.length > 1
    ? `${strings[0]}${countExpr}${pluralizedUnit}`
    : `${countExpr}${pluralizedUnit}`;
};
